import { useEffect } from 'react';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './cookie-consent.css';
import { run as CookieConsentRun } from 'vanilla-cookieconsent';
import { useFETelemetry } from '@talentinc/gatsby-theme-ecom/hooks/useTelemetry';
import useBrand from '@talentinc/gatsby-theme-ecom/hooks/useBrand';
import { useTranslation } from 'react-i18next';
import { isBrowser } from '@talentinc/gatsby-theme-ecom/utils/env';

export default function CookieConsentComponent() {
  const telemetry = useFETelemetry();
  const brand = useBrand();
  const { t } = useTranslation();

  useEffect(() => {
    CookieConsentRun({
      guiOptions: {
        consentModal: {
          layout: 'box wide',
          position: 'bottom center',
        },
      },
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {},
        marketing: {},
      },
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              acceptAllBtn: t('cookieBanner.consentModal.acceptAllBtn'),
              acceptNecessaryBtn: t('cookieBanner.consentModal.rejectAllBtn'),
              description: t('cookieBanner.consentModal.description', {
                brandDefaultPrivacyEmail: brand.defaultPrivacyEmail,
                brandUrl: brand.productionUrl,
              }),
              showPreferencesBtn: t(
                'cookieBanner.consentModal.showPreferencesBtn'
              ),
              title: t('cookieBanner.consentModal.title'),
            },
            preferencesModal: {
              acceptAllBtn: t('cookieBanner.preferencesModal.acceptAllBtn'),
              acceptNecessaryBtn: t(
                'cookieBanner.preferencesModal.rejectAllBtn'
              ),
              closeIconLabel: t('cookieBanner.preferencesModal.closeIconLabel'),
              savePreferencesBtn: t(
                'cookieBanner.preferencesModal.savePreferencesBtn'
              ),
              sections: t('cookieBanner.preferencesModal.sections', {
                returnObjects: true,
                defaultValue: [],
                brandName: brand.name,
                brandDefaultPrivacyEmail: brand.defaultPrivacyEmail,
                brandUrl: brand.productionUrl,
              }) as CookieConsent.Section[],
              title: t('cookieBanner.preferencesModal.title'),
            },
          },
        },
      },
      onModalShow: ({ modalName }) => {
        if (modalName == 'consentModal') return;
        telemetry.track({
          event: 'open_cookie_banner_settings',
        });
      },
      onFirstConsent: () => {
        window.dataLayer = window.dataLayer || [];
        (window.dataLayer as any[])?.push([
          'consent',
          'default',
          updateConsentSettings([], false),
        ]);
      },
      onConsent: (consent) => {
        const categories = consent.cookie.categories;
        const hasAnalytics = categories.includes('analytics');
        const hasMarketing = categories.includes('marketing');

        (window.dataLayer as any[])?.push({
          event: 'cookie_choice',
          eventCategory: 'Cookie Choice',
          eventAction: [
            'Essential',
            ...(hasAnalytics ? ['Analytical'] : []),
            ...(hasMarketing ? ['Marketing'] : []),
          ].join(', '),
        });

        (window.dataLayer as any[])?.push([
          'consent',
          'update',
          updateConsentSettings(categories),
        ]);
      },
      onChange: (consent) => {
        (window.dataLayer as any[])?.push([
          'consent',
          'update',
          updateConsentSettings(consent.cookie.categories),
        ]);
      },
    });

    const updateConsentSettings = (
      categories: string[],
      track: boolean = true
    ) => {
      const hasAnalytics = categories.includes('analytics');
      const hasMarketing = categories.includes('marketing');
      const hasAcceptedAll = hasAnalytics && hasMarketing;
      let result, eventPayload;

      if (!hasAnalytics && !hasMarketing) {
        result = {
          ad_personalization: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          analytics_storage: 'denied',
        };
        eventPayload = {
          cookie_settings: 'Reject',
        };
      }

      if (hasAcceptedAll) {
        result = {
          ad_personalization: 'granted',
          ad_storage: 'granted',
          ad_user_data: 'granted',
          analytics_storage: 'granted',
        };
        eventPayload = {
          cookie_settings: 'Accept all',
        };
      }

      if (hasAnalytics && !hasMarketing) {
        result = {
          ad_personalization: 'denied',
          ad_storage: 'granted',
          ad_user_data: 'granted',
          analytics_storage: 'granted',
        };
        eventPayload = {
          cookie_settings: 'Analytical cookies',
        };
      }

      if (!hasAnalytics && hasMarketing) {
        result = {
          ad_personalization: 'granted',
          ad_storage: 'granted',
          ad_user_data: 'granted',
          analytics_storage: 'denied',
        };
        eventPayload = {
          cookie_settings: 'Marketing cookies',
        };
      }

      if (track && isBrowser && !localStorage.getItem('cookieConsentTracked')) {
        telemetry.track({
          event: 'click_cookie_banner',
          properties: eventPayload as { cookie_settings: string },
        });
        localStorage.setItem('cookieConsentTracked', 'true');
      }

      return result;
    };
  }, [telemetry, brand, t]);
  return null;
}
